import { Button } from "components/Button/Button";
import { IconCollection } from "components/Icon/IconCollection";
import PageContent from "components/Layout/PageContent";
import RecordTable from "components/Table/RecordTable";
import { TableColumn } from "components/Table/tableUtils";
import Tooltip from "components/Tooltip/Tooltip";
import { customObjectListKey } from "containers/Resources/screens/ResourcesSingle/ResourcesSingle";
import { Record } from "graphql/generated/graphql";
import { ReactNode } from "react";
import { CustomObjectListToolbar, RelationshipRecordToolbar } from "../ResourceSingleToolbar";
import "./SubrecordTable.scss";

interface SubrecordTableProps<T> {
    data: T[];
    uniqueKey: string;
    title: string;
    onAddClick?: () => any;
    isActionButtonDisabled?: boolean;
    refreshData: () => Promise<any>;
    columns: TableColumn[];
    emptyListText: string;
    initialQuery?: any;
    recordLabel?: string;
    updateQuery?: (changedQuery: any) => void;
    totalItemCount?: number;
    isAddButtonDisabled?: boolean;
    addButtonTooltip?: string;
    headerAction?: ReactNode;
    // Where the `headerAction` sits relative to the "Add" button in the header
    // action row. Default "before" keeps the historical order (headerAction then
    // Add). "after" renders the Add button first, then the headerAction — used by
    // the object_template attributes toolbar so "+ Add" leads and "Re-order"
    // follows (operator-task-fe-ot-attributes-reorder-button-redesign).
    headerActionPlacement?: "before" | "after";
    tableOptions?: {
        viewColumns?: boolean;
    };
    tableSettingsKey?: string;
}

interface AddButtonProps {
    onClick: () => void;
    isDisabled?: boolean;
    tooltip?: string;
}

export const AddButton = ({ onClick, isDisabled, tooltip }: AddButtonProps) => {
    const addButton = (
        <Button
            size="small"
            icon={IconCollection.plus}
            label="Add"
            variation="dark"
            onClick={onClick}
            disabled={isDisabled}
        />
    );
    return tooltip?.length ? <Tooltip overlay={tooltip}>{addButton}</Tooltip> : addButton;
};

export const SubrecordTable = <T extends Pick<Record, "id"> & { name?: string }>({
    data,
    uniqueKey,
    title,
    onAddClick,
    isActionButtonDisabled,
    refreshData,
    columns,
    emptyListText,
    initialQuery,
    recordLabel,
    updateQuery,
    totalItemCount,
    isAddButtonDisabled,
    addButtonTooltip,
    headerAction,
    headerActionPlacement = "before",
    tableOptions = {},
    tableSettingsKey,
}: SubrecordTableProps<T>) => {
    const addButtonElement = onAddClick ? (
        <AddButton
            onClick={onAddClick}
            isDisabled={isAddButtonDisabled || isActionButtonDisabled}
            tooltip={addButtonTooltip}
        />
    ) : null;
    return (
        <PageContent key={uniqueKey} className="subrecordList">
            {title && (
                <div className="subrecordHeader">
                    <h3>{title}</h3>
                    {(headerAction || addButtonElement) && (
                        <div className="subrecordHeaderActions">
                            {headerActionPlacement === "after" ? (
                                <>
                                    {addButtonElement}
                                    {headerAction}
                                </>
                            ) : (
                                <>
                                    {headerAction}
                                    {addButtonElement}
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}
            {data?.length ? (
                <RecordTable
                    data={data}
                    totalItemCount={totalItemCount ?? data?.length}
                    options={{
                        toolbar: "custom",
                        customLeftToolbar:
                            uniqueKey === customObjectListKey
                                ? (CustomObjectListToolbar as (props: any) => any)
                                : (RelationshipRecordToolbar as (props: any) => any),
                        density: "high",
                        viewColumns: false,
                        // Wide subrecord/CO lists scroll horizontally inside
                        // their own responsiveBase container (SubrecordTable.scss);
                        // pin a discoverable scrollbar to the viewport bottom so
                        // it is reachable without scrolling to the end of a long
                        // list. (operator-task-fe-wide-list-top-or-sticky-
                        // horizontal-scrollbar)
                        stickyHorizontalScrollbar: true,
                        ...tableOptions,
                    }}
                    refresh={refreshData}
                    columns={columns}
                    recordTypes={null}
                    actionButtonLabel={recordLabel}
                    isLoading={false}
                    isActionButtonDisabled={isActionButtonDisabled}
                    initialQuery={initialQuery}
                    updateQuery={updateQuery}
                    tableSettingsKey={tableSettingsKey}
                />
            ) : (
                <div className="subrecord-empty-list">{emptyListText}</div>
            )}
        </PageContent>
    );
};
